import Avatar from '@mui/material/Avatar';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Check, ChevronsUpDown, LogOut } from 'lucide-react';
import { useState } from 'react';
import { ROLES, useRoleStore, type Role } from '@/shared/session/roleStore';
import { useActingRole } from '@/shared/session/useActingRole';
import { useSession, useSignOut } from '@/shared/session/useSession';
import { initials } from './initials';

const OPTIONS = Object.entries(ROLES) as [Role, (typeof ROLES)[Role]][];

type TriggerProps = { compact: boolean; name: string; viewing: string; isOpen: boolean; onOpen: (el: HTMLElement) => void };

function AccountTrigger({ compact, name, viewing, isOpen, onOpen }: TriggerProps) {
  return (
    <Tooltip title={compact ? `${name}, ${viewing}` : ''} placement="right">
      <ButtonBase
        aria-label={`${name}, ${viewing}. Account menu`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={(e) => { onOpen(e.currentTarget); }}
        sx={{ width: compact ? 'auto' : '100%', gap: 1.25, p: 0.75, borderRadius: 1.5, justifyContent: 'flex-start', '&:hover': { bgcolor: 'surface.subtle' } }}
      >
        <Avatar aria-hidden sx={{ width: 30, height: 30, fontSize: '0.75rem', fontWeight: 600, bgcolor: 'surface.avatar', color: 'surface.avatarText' }}>
          {initials(name).toUpperCase()}
        </Avatar>
        {compact ? null : (
          <>
            <Stack sx={{ minWidth: 0, flexGrow: 1, textAlign: 'left' }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>{name}</Typography>
              <Typography variant="caption" noWrap sx={{ color: 'text.secondary', lineHeight: 1.3 }}>{viewing}</Typography>
            </Stack>
            <ChevronsUpDown size={15} aria-hidden style={{ flexShrink: 0, opacity: 0.6 }} />
          </>
        )}
      </ButtonBase>
    </Tooltip>
  );
}

/** Who is signed in and the role the portal acts in. In a demo the menu switches roles; it always signs out.
 * `compact` is the avatar alone, for the folded sidebar. */
export function AccountMenu({ compact = false }: { compact?: boolean }) {
  const { role, demo } = useActingRole();
  const setRole = useRoleStore((s) => s.setRole);
  const name = useSession().data?.name ?? ROLES[role].user;
  const signOut = useSignOut();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const viewing = `Viewing as ${ROLES[role].label}`;

  return (
    <>
      <AccountTrigger compact={compact} name={name} viewing={viewing} isOpen={anchor !== null} onOpen={setAnchor} />
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => { setAnchor(null); }}
        anchorOrigin={{ vertical: 'top', horizontal: compact ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ list: { subheader: demo ? <ListSubheader sx={{ fontSize: '0.75rem', lineHeight: 2.5, bgcolor: 'transparent' }}>Viewing as</ListSubheader> : undefined } }}
      >
        {(demo ? OPTIONS : []).map(([key, { label, user }]) => (
          <MenuItem key={key} selected={key === role} onClick={() => { setRole(key); setAnchor(null); }} sx={{ minWidth: 232 }}>
            <ListItemText primary={label} secondary={user} slotProps={{ secondary: { variant: 'caption' } }} />
            {key === role ? <Check size={16} aria-hidden /> : null}
          </MenuItem>
        ))}
        {demo ? <Divider /> : null}
        <MenuItem onClick={() => { setAnchor(null); signOut.mutate(); }}>
          <ListItemIcon><LogOut size={16} aria-hidden /></ListItemIcon>
          <ListItemText primary="Sign out" />
        </MenuItem>
      </Menu>
    </>
  );
}
